import {useState, useCallback} from 'react'
import {useServices} from '@/services/context'
import type {SqlExecute} from '@/pages/reporting/types'

/**
 * Hook for executing raw SQL queries via the reporting endpoint.
 * Used by SqlEditorDialog and ReportDefinitionEdit.
 */
export function useSqlRunner(maxRows: number, dryRun?: boolean) {
    const services = useServices()
    const [isRunning, setIsRunning] = useState(false)
    const [result, setResult] = useState<SqlExecute | null>(null)
    const [error, setError] = useState<Error | null>(null)

    const run = useCallback(async (sql: string) => {
        if (!sql.trim()) return
        setIsRunning(true)
        setError(null)
        setResult(null)
        try {
            const data = await services.reporting.sqlExecute({sql: sql.trim(), maxRows, dryRun: dryRun ?? false}) as SqlExecute
            setResult(data)
        } catch (e) {
            setError(e instanceof Error ? e : new Error(String(e)))
        } finally {
            setIsRunning(false)
        }
    }, [maxRows, dryRun, services.reporting])

    const reset = useCallback(() => {
        setResult(null)
        setError(null)
    }, [])

    return {isRunning, result, error, run, reset}
}
